import React from 'react';
import { X } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Button, FieldLabel, IconButton, Select, TextInput } from '../../components/Primitives.jsx';
import { ColumnsContext, Segmented } from '../StepInspector.jsx';
import { ColSelect } from '../shared/ColSelect.jsx';
import { MultiCols } from '../shared/MultiCols.jsx';
import { ConditionRows } from '../shared/ConditionRows.jsx';
import { DecimalPlacesField } from '../shared/DecimalPlacesField.jsx';

const RULE_TYPES = [
  { value: 'limit', label: 'Cumulative limit' },
  { value: 'change', label: 'Value changes' },
  { value: 'condition', label: 'Condition' },
];
const LIMIT_FNS = [
  { value: 'sum', label: 'sum' },
  { value: 'count', label: 'count of rows' },
  { value: 'max', label: 'max' },
  { value: 'avg', label: 'average' },
];

// Mirrors GROUP_AGG_FNS / group_agg_target in steps/aggregate.py.
const AGG_FNS = [
  { value: 'sum', label: 'sum' },
  { value: 'avg', label: 'average' },
  { value: 'min', label: 'min' },
  { value: 'max', label: 'max' },
  { value: 'count', label: 'count of rows' },
  { value: 'count_values', label: 'count of values' },
  { value: 'count_distinct', label: 'count distinct' },
  { value: 'first', label: 'first value' },
  { value: 'last', label: 'last value' },
];
const aggTarget = (a) => ((a.target || '').trim()
  || ((a.fn || 'sum') === 'count' ? 'count_rows' : `${a.fn || 'sum'}_${a.column || ''}`));

const card = {
  background: C.input, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: 10, marginTop: 8,
};

// ── Conditional Group ────────────────────────────────────────────────────
// Writes a group code onto every row (rows are kept, unlike Group By).
// Rows share a code when their group-by columns match and no control-break
// rule fired between them, walking each key's rows in the chosen order.
// Mirrors s_conditional_group (server/app/engine/steps/aggregate.py).
export function ConditionalGroupInspector({ step, onChange, columns, secondaryDatasets, sheets, pipeline, runMap, onBrowseJoinFile }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const liveCols = React.useContext(ColumnsContext);
  const cols = columns || liveCols || [];
  const firstCol = cols[0]?.name || '';

  const order = c.order || [];
  const setOrder = (next) => u({ order: next });
  const breaks = c.breaks || [];
  const setBreaks = (next) => u({ breaks: next });
  const updateBreak = (i, patch) => setBreaks(breaks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const newRule = (type) => (
    type === 'limit' ? { type, column: firstCol, fn: 'sum', limit: '' }
      : type === 'change' ? { type, column: firstCol }
        : { type, conditions: [{ column: firstCol, op: 'eq', value: '' }], conjunction: 'AND' }
  );
  const aggregates = c.aggregates || [];
  const setAggregates = (next) => u({ aggregates: next });
  const updateAgg = (i, patch) => setAggregates(aggregates.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const hasLimitTotals = breaks.some(b => b.type === 'limit' && ((b.running_column || '').trim() || (b.total_column || '').trim()))
    || aggregates.some(a => !['first', 'last'].includes(a.fn || 'sum'));

  const sourceCtx = {
    secondaryDatasets, sheets, pipeline, currentStep: step, runMap,
    onBrowse: onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined,
    buildTargetName: pipeline?.inputFile?.buildTargetName,
  };

  return (
    <div>
      <FieldLabel hint="Name of the new column that holds each row's group code.">Group code column</FieldLabel>
      <TextInput mono value={c.target ?? ''} onChange={(v) => u({ target: v })} placeholder="GroupCode" />

      <FieldLabel hint="Rows only share a code when these columns match. Leave empty to treat all rows as one key.">Group by</FieldLabel>
      <MultiCols columns={cols} selected={c.keys || []} onChange={(v) => u({ keys: v })} />

      <FieldLabel hint="Order of rows inside each key while looking for control breaks. Empty keeps file order. Row order in the output never changes.">Order within group</FieldLabel>
      {order.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: i ? 6 : 0 }}>
          <ColSelect columns={cols} value={o.column} onChange={(v) => setOrder(order.map((x, j) => (j === i ? { ...x, column: v } : x)))} style={{ flex: 1, minWidth: 0 }} />
          <Select value={o.dir || 'asc'} onChange={(v) => setOrder(order.map((x, j) => (j === i ? { ...x, dir: v } : x)))}
            options={[{ value: 'asc', label: '↑ asc' }, { value: 'desc', label: '↓ desc' }]} style={{ width: 90 }} />
          <IconButton title="Remove" onClick={() => setOrder(order.filter((_, j) => j !== i))}><X size={14} /></IconButton>
        </div>
      ))}
      <Button kind="ghost" size="sm" icon="＋" style={{ marginTop: 6 }}
        onClick={() => setOrder([...order, { column: firstCol, dir: 'asc' }])}>Add sort column</Button>

      <FieldLabel hint="Any rule that fires starts a new group code at that row.">Control breaks</FieldLabel>
      {breaks.length === 0 && (
        <div style={{ color: C.fg5, fontSize: '.78rem' }}>No breaks: one code per group-by key.</div>
      )}
      {breaks.map((b, i) => (
        <div key={i} style={card}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: C.fg4, fontSize: '.74rem', fontWeight: 600 }}>{i + 1}</span>
            <Select value={b.type} onChange={(v) => setBreaks(breaks.map((x, j) => (j === i ? newRule(v) : x)))}
              options={RULE_TYPES} style={{ flex: 1 }} />
            <IconButton title="Remove rule" onClick={() => setBreaks(breaks.filter((_, j) => j !== i))}><X size={14} /></IconButton>
          </div>

          {b.type === 'limit' && (
            <>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                <Select value={b.fn || 'sum'} onChange={(v) => updateBreak(i, { fn: v })} options={LIMIT_FNS} style={{ width: 130 }} />
                {(b.fn || 'sum') !== 'count' && (
                  <ColSelect columns={cols} value={b.column} onChange={(v) => updateBreak(i, { column: v })} style={{ flex: 1, minWidth: 0 }} />
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                <span style={{ color: C.fg4, fontSize: '.78rem', whiteSpace: 'nowrap' }}>new group when over</span>
                <TextInput mono value={b.limit ?? ''} onChange={(v) => updateBreak(i, { limit: v })} placeholder="26" />
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <TextInput mono value={b.running_column ?? ''} onChange={(v) => updateBreak(i, { running_column: v })} placeholder={b.fn === 'avg' ? 'Running average column (optional)' : 'Running total column (optional)'} />
                <TextInput mono value={b.total_column ?? ''} onChange={(v) => updateBreak(i, { total_column: v })} placeholder={b.fn === 'avg' ? 'Group average column (optional)' : 'Group total column (optional)'} />
              </div>
            </>
          )}

          {b.type === 'change' && (
            <div style={{ marginTop: 8 }}>
              <ColSelect columns={cols} value={b.column} onChange={(v) => updateBreak(i, { column: v })} />
            </div>
          )}

          {b.type === 'condition' && (
            <div style={{ marginTop: 8 }}>
              {(b.conditions || []).length > 1 && (
                <Segmented value={b.conjunction || 'AND'} onChange={(v) => updateBreak(i, { conjunction: v })}
                  options={[{ value: 'AND', label: 'ALL match' }, { value: 'OR', label: 'ANY match' }]} />
              )}
              <ConditionRows
                conditions={b.conditions || []}
                columns={cols}
                onUpdate={(k, patch) => updateBreak(i, { conditions: (b.conditions || []).map((x, j) => (j === k ? { ...x, ...patch } : x)) })}
                onRemove={(k) => updateBreak(i, { conditions: (b.conditions || []).filter((_, j) => j !== k) })}
                variant="plain"
                sourceCtx={sourceCtx}
              />
              <Button kind="ghost" size="sm" icon="＋"
                onClick={() => updateBreak(i, { conditions: [...(b.conditions || []), { column: firstCol, op: 'eq', value: '' }] })}>
                Add condition
              </Button>
            </div>
          )}
        </div>
      ))}
      <Button kind="ghost" size="sm" icon="＋" style={{ marginTop: 8 }}
        onClick={() => setBreaks([...breaks, newRule('limit')])}>Add break rule</Button>

      <FieldLabel hint="Each one is computed over the rows of a resulting group (after the breaks) and written to every row of that group.">Group aggregates</FieldLabel>
      {aggregates.length === 0 && (
        <div style={{ color: C.fg5, fontSize: '.78rem' }}>None: only the group code is added.</div>
      )}
      {aggregates.map((a, i) => (
        <div key={i} style={card}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Select value={a.fn || 'sum'} onChange={(v) => updateAgg(i, { fn: v })} options={AGG_FNS} style={{ width: 140 }} />
            {(a.fn || 'sum') !== 'count' && (
              <ColSelect columns={cols} value={a.column} onChange={(v) => updateAgg(i, { column: v })} style={{ flex: 1, minWidth: 0 }} />
            )}
            {(a.fn || 'sum') === 'count' && <div style={{ flex: 1 }} />}
            <IconButton title="Remove aggregate" onClick={() => setAggregates(aggregates.filter((_, j) => j !== i))}><X size={14} /></IconButton>
          </div>
          <div style={{ marginTop: 6 }}>
            <TextInput mono value={a.target ?? ''} onChange={(v) => updateAgg(i, { target: v })} placeholder={`Output column (default ${aggTarget({ ...a, target: '' })})`} />
          </div>
        </div>
      ))}
      <Button kind="ghost" size="sm" icon="＋" style={{ marginTop: 8 }}
        onClick={() => setAggregates([...aggregates, { fn: 'sum', column: firstCol, target: '' }])}>Add aggregate</Button>

      <FieldLabel>Numbering</FieldLabel>
      <Segmented value={c.numbering || 'global'} onChange={(v) => u({ numbering: v })}
        options={[{ value: 'global', label: '1…N across all rows' }, { value: 'per_key', label: 'Restart per key' }]} />

      {hasLimitTotals && (
        <DecimalPlacesField defaultPlaces={null} value={c.output_decimals} onChange={(v) => u({ output_decimals: v })} />
      )}

      <Alert kind="info" style={{ marginTop: 12 }}>
        Every row is kept in its original position. A row that is over a limit on
        its own gets its own code and a warning.
      </Alert>
    </div>
  );
}
